import {useState} from 'react';
import {Form, Input, InputNumber, Select, Switch, Tabs} from 'antd';
import {Button} from '@dabafinance/react-components';
import UploadImage from 'components/Startup/profile/companyImage';
import {supportedLanguages} from 'utils/constants';
import {BenefitsSection} from './BenefitsSection';
import {RichTextEditor} from 'components/richTextEditor';
import {AnnouncementIconUpload} from './AnnouncementIconUpload';
import {ANNOUNCEMENT_BUCKET} from './constants';
import styles from './fullScreenPopup.module.scss';

// Renders the localized text inputs for either the original language or the
// translation. `multilang` flips the field path to the `multilang` object.
const renderTextFields = multilang => {
  const scope = multilang ? ['multilang'] : [];
  const suffix = multilang ? ' (translated)' : '';
  return (
    <>
      <Form.Item
        label="Title"
        className={styles.fieldWidth}
        name={[...scope, 'title']}
        rules={
          multilang ? [] : [{required: true, message: 'Title is required'}]
        }>
        <Input placeholder={`Enter title${suffix}`} />
      </Form.Item>
      <Form.Item
        label="Subtitle"
        className={styles.fieldWidth}
        name={[...scope, 'subTitle']}>
        <RichTextEditor
          className={styles.quill}
          placeholder={`Enter subtitle${suffix}`}
        />
      </Form.Item>
      <Form.Item
        label="Header Badge Text"
        className={styles.fieldWidth}
        name={[...scope, 'headerBadgeText']}
        tooltip="Small badge shown at the top of the popup (e.g. 'Limited offer').">
        <Input placeholder={`Enter header badge text${suffix}`} />
      </Form.Item>
      <Form.Item
        label="Footer Text"
        className={styles.fieldWidth}
        name={[...scope, 'footerText']}>
        <Input.TextArea rows={2} placeholder={`Enter footer text${suffix}`} />
      </Form.Item>
      <Form.Item
        label="Close Button Label"
        className={styles.fieldWidth}
        name={[...scope, 'closeButtonLabel']}
        tooltip="Label for the dismiss button (e.g. 'Maybe later').">
        <Input placeholder={`Enter close button label${suffix}`} />
      </Form.Item>
      <Form.Item
        label="Action Button Label"
        className={styles.fieldWidth}
        name={[...scope, 'actionButtonLabel']}
        tooltip="Label for the primary call-to-action button.">
        <Input placeholder={`Enter action button label${suffix}`} />
      </Form.Item>
    </>
  );
};

export const StepFullScreenContent = ({form, popup}) => {
  const {Option} = Select;
  const {handleTranslate, loadingTranslation} = popup;

  const rootLanguage = Form.useWatch('language', form);
  const translation = Form.useWatch('translation', form);
  const benefits = Form.useWatch('benefits', form) || [];

  const [coverPreview, setCoverPreview] = useState(
    form.getFieldValue('imageUrl') || null,
  );

  return (
    <div className={styles.stepContainer}>
      <h2>Popup Content</h2>
      <p>
        Configure the content shown inside the full screen popup, including
        benefits and (for Daba Pro / Daba Academy) the subscription renewal
        offer.
      </p>

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <h3>Cover Image &amp; Icon</h3>
          <span>The hero image and icon displayed on the popup.</span>
        </div>
        <UploadImage
          previewUrl={coverPreview}
          setPreviewUrl={setCoverPreview}
          setImgUrl={url => form.setFieldValue('imageUrl', url)}
          large
          bucket={ANNOUNCEMENT_BUCKET}
        />
        <Form.Item
          label="Announcement Icon"
          name="announcementIcon"
          className={styles.iconFormItem}
          tooltip="Small icon representing this announcement.">
          <AnnouncementIconUpload />
        </Form.Item>
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <h3>Content & Translation</h3>
          <span>
            Fill in the original language content. Optionally pick a translation
            language and auto-translate.
          </span>
        </div>
        <Form.Item
          label="Translation Language"
          className={styles.fieldWidth}
          name="translation">
          <Select allowClear placeholder="Select a language to translate to">
            {supportedLanguages
              .filter(language => language.value !== rootLanguage)
              .map(language => (
                <Option key={language.value} value={language.value}>
                  {language.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Tabs
          className={styles.tabs}
          tabBarExtraContent={
            translation ? (
              <Button
                disabled={loadingTranslation}
                label={loadingTranslation ? 'Translating...' : 'Translate'}
                onClick={handleTranslate}
              />
            ) : null
          }>
          <Tabs.TabPane tab="Original" key="original">
            {renderTextFields(false)}
            <Form.Item
              label="Deeplink URL"
              className={styles.fieldWidth}
              name="deeplinkUrl"
              tooltip="Where the action button takes the user (deep link or URL)."
              rules={[{type: 'url', message: 'Please enter a valid URL'}]}>
              <Input placeholder="https:// or app deeplink" />
            </Form.Item>
            <Form.Item
              label="Popup Content"
              name="popUpContent"
              tooltip="Rich text body for the popup. Inserted images upload to our storage (not embedded as base64).">
              <RichTextEditor
                withImage
                className={styles.contentQuill}
                placeholder="Write the popup content..."
              />
            </Form.Item>
          </Tabs.TabPane>
          {translation ? (
            <Tabs.TabPane
              forceRender
              tab={
                supportedLanguages.find(
                  language => language.value === translation,
                )?.label || translation
              }
              key={translation}>
              {renderTextFields(true)}
              <Form.Item
                label="Popup Content (translated)"
                name={['multilang', 'popUpContent']}>
                <RichTextEditor
                  withImage
                  className={styles.contentQuill}
                  placeholder="Translated popup content..."
                />
              </Form.Item>
              {benefits.length > 0 ? (
                <div className={styles.translatedFeatures}>
                  <p className={styles.translatedFeaturesLabel}>
                    Feature labels (translated)
                  </p>
                  {benefits.map((benefit, index) => (
                    <Form.Item
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className={styles.fieldWidth}
                      label={`Feature ${index + 1}${
                        benefit?.label ? ` — ${benefit.label}` : ''
                      }`}
                      name={['multilang', 'benefits', index, 'label']}>
                      <Input placeholder="Translated label" />
                    </Form.Item>
                  ))}
                </div>
              ) : null}
            </Tabs.TabPane>
          ) : null}
        </Tabs>
      </div>

      <BenefitsSection />

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <h3>Behaviour</h3>
          <span>How the popup behaves once shown to the user.</span>
        </div>
        <Form.Item
          label="Dismissible?"
          name="dismissible"
          valuePropName="checked"
          tooltip="Allow the user to dismiss the popup.">
          <Switch />
        </Form.Item>
        <Form.Item
          label="Max Show Count"
          name="maxShowCount"
          tooltip="How many times the popup can be shown to a user.">
          <InputNumber min={1} max={100} className="input-number" />
        </Form.Item>
      </div>
    </div>
  );
};
